import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, take, timeout } from 'rxjs/operators';
import { ApiService } from './api.service';

/**
 * Route guard that gates a route on the *current network's* extension list. Pair with
 * route data, e.g.:
 *
 *   { path: 'network/ipsec-tunnelers',
 *     canActivate: [NetworkExtensionGuard],
 *     data: { requiresExtension: NETWORK_EXTENSIONS.IPSEC_TUNNELER },
 *     loadChildren: ... }
 *
 * Resolution:
 *   - `apiService.currentNetwork` is a BehaviorSubject; we wait for the first non-empty
 *     emission (the empty-object seed value is dropped via the `.id` truthy check) so
 *     navigating directly into a route before the network has loaded still works.
 *   - When the extension is present, returns true and the route activates normally.
 *   - When absent (or extensions weren't embedded — same effective result), returns a
 *     UrlTree to the network dashboard so the user lands somewhere sensible rather than
 *     a 404 page.
 *
 * The companion server-side gate is `@RequiresNetworkExtension` on the controller, so a
 * user who hand-rolls the URL still gets 404s on the API and won't see anything useful.
 * This guard is purely a UX nicety to keep the SPA from rendering a dead route.
 */
@Injectable({ providedIn: 'root' })
export class NetworkExtensionGuard implements CanActivate {
    constructor(private apiService: ApiService, private router: Router) {}

    canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
        const required = route.data?.['requiresExtension'] as string | undefined;
        if (!required) return of(true);

        // Filter requires BOTH .id AND a resolved .extensions array. The bulk-list path
        // emits a network with `extensions: undefined`; accepting that emit would make the
        // guard bounce extension-gated routes on every reload (silent no-extension state).
        // api.service.ts:setCurrentNetwork re-fetches with embed=extensions and re-pushes,
        // so a later emit will have the array populated. Filter on that.
        //
        // Fail-open timeout: if currentNetwork doesn't emit with extensions within 3s,
        // allow the route through. The network-fetch flow on cold reload to an extension-
        // gated route can be chicken-and-egg (network fetch is triggered after route
        // activation kicks off the auth resolver chain, but the guard blocks route
        // activation). Letting the route through after the timeout breaks the deadlock;
        // server-side `@RequiresNetworkExtension` is the actual gate — this guard is a UX
        // nicety per the class comment above.
        return this.apiService.currentNetwork.pipe(
            filter((n: any) => !!n?.id && Array.isArray(n?.extensions)),
            take(1),
            timeout(3000),
            map((network: any) => {
                const extensions: { extension: string }[] = network?.extensions ?? [];
                const enabled = extensions.some((e) => e.extension === required);
                if (enabled) return true as boolean | UrlTree;
                const redirect =
                    (route.data?.['extensionMissingRedirect'] as string | undefined) ?? '/network/dashboard';
                return this.router.createUrlTree([redirect]);
            }),
            catchError(() => of(true as boolean | UrlTree))
        );
    }
}
